/**
 * 岗位性质 字典(value -> name).
 *
 * 产品约定:前端只展示/可选"全职"和"临时工"两种；
 * 后端若返回字典外的 value(如 '23' 日结),展示层会**原样显示数字**,
 * 用于暴露脏数据/字典漂移,不静默吞掉。
 *
 * 已知映射:
 *   ✓ '11' → 全职   (来自 intentionTransforms.test.js 与真实用户数据)
 *   ⚠️ '22' → 临时工 (按旧代码 item.PROPERTY === '2' 推断的升级编码,需后端确认)
 *
 * 注意:
 *   - 这里的 value 必须与后端 intentions[*].PROPERTY[*].value 完全一致
 *   - 这里同时被 IntentionsView 的展示 + formList 的 options 共用
 *   - 新增/修改只需在此处改一处
 */
export const PROPERTY_OPTIONS = [
  { label: '全职',   value: '11' },
  { label: '临时工', value: '22' },
];

// 反向索引(value → 中文名),给 IntentionsView.js 在 buildDisplayData 中复用.
// 选型说明:用 Map 而不是普通对象,因为 Map 查找复杂度永远是 O(1),
// 对一个 5~20 项的字典两者没差别,但 Map 在类型表达上更"这就是字典"。
const PROPERTY_NAME_BY_VALUE = new Map(PROPERTY_OPTIONS.map((o) => [o.value, o.label]));

/** 把后端 value 转中文名(找不到时返回原 value,便于排查乱码数据)。 */
export const getPropertyName = (value) => PROPERTY_NAME_BY_VALUE.get(value) || value || '—';

export const baseInfoSection = {
  id: 'baseInfo',
  title: '基本信息',
  type: 'single',
  fields: [
    { label: '姓名', props: 'PERNAME' },
    { label: '性别', props: 'SEX_CN' },
    { label: '当前身份', props: 'IDENTITY_CN' },
    { label: '年龄', props: 'AGE' },
    { label: '参加工作时间', props: 'WKDATE' },
    { label: '户口所在地', props: 'REGDISTRICT_CN' },
    { label: '现居住城市', props: 'RESDISTRICT_CN' },
    { label: '政治面貌', props: 'PLSTAND_CN' },
    { label: '联系方式', props: 'CELL' },
    { label: '联系邮箱', props: 'EMAIL' },
  ],
  formList: [
    { label: '姓名', type: 'input', props: 'PERNAME', required: true, message: '请输入姓名', placeholder: '请输入姓名' },
    { label: '性别', type: 'radio', props: 'SEX', required: true, message: '请选择性别', options: [{ label: '男', value: '1' }, { label: '女', value: '2' }] },
    { label: '当前身份', type: 'radio', props: 'IDENTITY', optionKey: 'identityOptions' },
    { label: '出生年月', type: 'DatePicker', props: 'BRTHDATE', required: true, message: '请选择出生年月' },
    { label: '参加工作时间', type: 'DatePicker', props: 'WKDATE' },
    { label: '户口所在地', type: 'cascader', props: 'REGDISTRICT', optionKey: 'areaList', placeholder: '请选择户口所在地' },
    { label: '现居住城市', type: 'cascader', props: 'RESDISTRICT', optionKey: 'areaList', placeholder: '请选择现居住城市' },
    { label: '政治面貌', type: 'select', props: 'PLSTAND', optionKey: 'plstandOptions' },
    { label: '联系方式', type: 'input', props: 'CELL', disabled: true, placeholder: '请输入电话号码' },
    { label: '联系邮箱', type: 'input', props: 'EMAIL', placeholder: '请输入邮箱' },
  ],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 16 } },
};

export const advantageSection = {
  id: 'ADVANTAGE',
  title: '个人优势',
  type: 'single',
  emptyText: '请输入您个人优势，例如：工作几年，熟悉哪个行业，自己的优势有哪些。',
  fields: [{ label: '个人优势', props: 'ADVANTAGE' }],
  formList: [{ label: '个人优势', type: 'textarea', props: 'ADVANTAGE', placeholder: '请输入个人优势' }],
  formProps: { wrapperCol: { span: 24 } },
};

export const statusSection = {
  id: 'status',
  title: '求职状态',
  type: 'single',
  fields: [{ label: '求职状态', props: 'STATUS_CN' }],
  formList: [{ label: '求职状态', type: 'select', props: 'STATUS', optionKey: 'statusOptions', placeholder: '请选择求职状态' }],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 12 } },
};

export const intentionSection = {
  id: 'jobInfo',
  title: '求职意向',
  tips: '您最多可添加 5 条意向',
  type: 'list',
  listKey: 'intention',
  maxCount: 5,
  // displayFields: 仅用于旧 ListSectionView 路径, 新 IntentionsView 用 buildDisplayData 自管展示
  displayFields: [
    { label: '期望岗位', props: 'JOBTYPE' },
    { label: '期望城市', props: 'DISTRICT' },
    { label: '期望月薪', props: 'SALFROM', type: 'salary', unit: '元' },
    { label: '工作性质', props: 'PROPERTY_CN' },
  ],
  // formList: JOBTYPE / DISTRICT 字段直接是 inputBlur, 内部用对象
  //   写入: 弹窗确认 → IntentionModule 写 item.JOBTYPE = { name, value } → IntentionsView
  //         在 initialValues 把 .name 字符串灌进 form 字段供显示
  //   读取: form submit 时, IntentionsView 用 list[idx].JOBTYPE 对象覆盖回 form 字段,
  //         buildIntention 拿 .value 提交
  formList: [
    { label: '期望岗位', type: 'inputBlur', props: 'JOBTYPE', trigger: 'intentionJob', placeholder: '请选择期望岗位' },
    { label: '岗位性质', type: 'select', props: 'PROPERTY', placeholder: '请选择岗位性质', options: PROPERTY_OPTIONS },
    { label: '期望城市', type: 'inputBlur', props: 'DISTRICT', trigger: 'intentionArea', placeholder: '请选择期望城市' },
    { label: '期望月薪', type: 'inputNumbers', props: 'SALFROM', moreProps: 'SALTO', placeholder: '期望月薪(元)', unit: '元' },
  ],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 20 } },
};

export const occupationSection = {
  id: 'workHistory',
  title: '工作/实习经历',
  type: 'list',
  listKey: 'occupation',
  formList: [
    { label: '公司名称', type: 'input', props: 'ENTNAME', placeholder: '请输入公司名称' },
    { label: '岗位类型', type: 'inputBlur', props: 'JOBTYPE_CN', hiddenValueProp: 'JOBTYPE', trigger: 'occupationJobType', placeholder: '请选择岗位类型' },
    { label: '职位名称', type: 'input', props: 'POSITION', placeholder: '请输入职位名称' },
    { label: '在职时间', type: 'customDate', props: 'OCCDATE', fromProps: 'OCCFROM', toProps: 'OCCTO', placeholder: '请选择' },
    { label: '当前月薪', type: 'inputNumber', props: 'SAL', placeholder: '请输入月薪(元)', unit: '元' },
    { label: '工作描述', type: 'textarea', props: 'DETAIL', placeholder: '请输入工作描述' },
  ],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 20 } },
};

export const educationSection = {
  id: 'EdcHistory',
  title: '教育经历',
  type: 'list',
  listKey: 'education',
  formList: [
    { type: 'tips', props: 'educationTips', text: '请填写国家承认的教育经历，非培训经历' },
    { label: '选择学历', type: 'select', props: 'EDU', optionKey: 'eduOptions', placeholder: '请选择学历' },
    { label: '学校名称', type: 'input', props: 'SCHOOL', placeholder: '请输入学校名称' },
    { label: '教育类型', type: 'radio', props: 'ENRTYPE', options: [{ label: '统招', value: '1' }, { label: '非统招', value: '2' }, { label: '海外', value: '3' }] },
    { label: '学位证书', type: 'radio', props: 'EDUDC', options: [{ label: '有', value: '1' }, { label: '无', value: '0' }] },
    { label: '所学专业', type: 'input', props: 'MAJOR', placeholder: '请输入专业名称' },
    { label: '在校时间', type: 'customDate', props: 'ENRDATE', fromProps: 'ENRFROM', toProps: 'ENRTO', placeholder: '请选择' },
  ],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 20 } },
};

export const skillSection = {
  id: 'jobSkill',
  title: '专业技能',
  type: 'list',
  listKey: 'skill',
  formList: [
    { label: '技能名称', type: 'input', props: 'SKLNAME', placeholder: '请输入技能名称' },
    { label: '熟练程度', type: 'radio', props: 'PROFLVL', optionKey: 'skilledOptions' },
  ],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 20 } },
};

export const certificateSection = {
  id: 'certificate',
  title: '资格证书',
  type: 'list',
  listKey: 'certificate',
  formList: [
    { label: '证书名称', type: 'input', props: 'CERTNAME', placeholder: '请输入证书名称' },
    { label: '上传材料', type: 'certificateFile', props: 'certificate' },
  ],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 20 } },
};

export const languageSection = {
  id: 'language',
  title: '语言能力',
  type: 'list',
  listKey: 'language',
  formList: [
    { label: '语言种类', type: 'select', props: 'LANGUAGE', optionKey: 'languageOptions', placeholder: '请选择语言种类' },
    { label: '听说能力', type: 'radio', props: 'SPEAKING', optionKey: 'skilledOptions' },
    { label: '读写能力', type: 'radio', props: 'LITERACY', optionKey: 'skilledOptions' },
  ],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 20 } },
};

export const worksSection = {
  id: 'works',
  title: '个人作品',
  tips: '您最多可在此上传10组附件',
  type: 'list',
  listKey: 'works',
  formList: [
    { label: '作品名称', type: 'input', props: 'WORKSNAME', placeholder: '请输入作品名称' },
    { label: '上传材料', type: 'worksFile', props: 'works' },
  ],
  formProps: { labelCol: { span: 4 }, wrapperCol: { span: 20 } },
};

export const resumeSections = [
  baseInfoSection,
  advantageSection,
  statusSection,
  intentionSection,
  occupationSection,
  educationSection,
  skillSection,
  certificateSection,
  languageSection,
  worksSection,
];

export const singleSectionIds = resumeSections.filter((section) => section.type === 'single').map((section) => section.id);
export const listSectionIds = resumeSections.filter((section) => section.type === 'list').map((section) => section.id);
